/**
 * Centralised JSON-LD generators for Schema.org SEO.
 *
 * Every page in the public site imports one or more constants from this file
 * and feeds them to <SEO jsonLd={...}/>, which serialises them as
 * `<script type="application/ld+json">` tags inside <head>.
 *
 * Why this file matters for SEO:
 *  - Restaurant + LocalBusiness + TouristAttraction multi-type lets Google
 *    surface the place card with hours, ratings, awards and reservation CTA.
 *  - Menu schema (built dynamically from the live DB carta via buildMenuLd)
 *    powers the "Menu" rich result.
 *  - Museum + TouristAttraction schema reinforces local tourism intent.
 *  - FAQ_LD is eligible for the "People also ask" rich result.
 *  - BreadcrumbList (built inside SEO.js per page) shows the path in SERPs.
 *
 * NB: The production hostname is intentionally hard-coded. The preview deploy
 * still uses the canonical production URL for SEO consistency.
 */

export const SITE_URL = "https://restaurantemuseoteneria.es";
export const RESTAURANT_NAME = "Restaurante Museo Tenería";

export const AREA_SERVED = [
    "Ocaña",
    "Aranjuez",
    "Tarancón",
    "Noblejas",
    "Villatobas",
    "Yepes",
    "Dos Barrios",
    "Santa Cruz de la Zarza",
    "La Guardia",
    "Lillo",
    "Mesa de Ocaña",
    "La Sagra",
    "Comarca de Ocaña",
    "Toledo",
    "Madrid Sur",
];

export const RESTAURANT_LD = {
    "@context": "https://schema.org",
    "@type": ["Restaurant", "LocalBusiness", "TouristAttraction"],
    "@id": `${SITE_URL}/#restaurant`,
    name: RESTAURANT_NAME,
    alternateName: ["Tenería Ocaña", "Restaurante Tenería"],
    image: [`${SITE_URL}/og-default.jpg`],
    logo: `${SITE_URL}/logo.png`,
    url: SITE_URL,
    telephone: "+34925484633",
    email: "restaurantemuseoteneria@gmail.com",
    description:
        "Restaurante Museo Tenería en Ocaña (Toledo). Edificio único del siglo XV, antigua fábrica de guantes de perrillo. Cocina tradicional manchega, parrilla a la brasa de encina, chuletón premiado. Visitas guiadas al museo.",
    address: {
        "@type": "PostalAddress",
        streetAddress: "Plaza del Mercado 3",
        addressLocality: "Ocaña",
        addressRegion: "Toledo",
        postalCode: "45300",
        addressCountry: "ES",
    },
    geo: {
        "@type": "GeoCoordinates",
        latitude: 39.9573,
        longitude: -3.5024,
    },
    hasMap: "https://www.google.com/maps/search/?api=1&query=Restaurante+Museo+Tener%C3%ADa+Plaza+del+Mercado+3+Oca%C3%B1a",
    areaServed: AREA_SERVED.map((name) => ({ "@type": "City", name })),
    servesCuisine: ["Manchega", "Mediterránea", "Tradicional española", "Parrilla", "Asador"],
    priceRange: "€€€",
    paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
    currenciesAccepted: "EUR",
    acceptsReservations: "True",
    aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.7",
        reviewCount: "1273",
        bestRating: "5",
        worstRating: "1",
    },
    award: [
        "Nº 1 TripAdvisor Ocaña",
        "Premio Restaurant Guru a la mejor parrilla",
        "Mención ARQUEOPRAXIS por rehabilitación ejemplar",
    ],
    openingHoursSpecification: [
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Wednesday", "Sunday"], opens: "11:00", closes: "16:30" },
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Thursday", "Friday", "Saturday"], opens: "11:00", closes: "16:30" },
        { "@type": "OpeningHoursSpecification", dayOfWeek: ["Thursday", "Friday", "Saturday"], opens: "20:00", closes: "23:00" },
    ],
    sameAs: [
        "https://www.facebook.com/Restaurante-Museo-Tener%C3%ADa-386110368612753",
        "https://www.tripadvisor.es/Restaurant_Review-Oca%C3%B1a-Tener%C3%ADa.html",
    ],
    hasMenu: `${SITE_URL}/gastronomia`,
    knowsAbout: [
        "Cocina manchega",
        "Parrilla de encina",
        "Chuletón maduración propia",
        "Vinos La Mancha",
        "Historia de Ocaña",
        "Guantes de perrillo",
        "Judería de Ocaña",
        "Edificio siglo XV",
    ],
};

export function buildMenuLd(carta, chuletonPrecio) {
    const sections = (carta || []).map((sec) => {
        if (sec.tipo === "grupos") {
            return {
                "@type": "MenuSection",
                name: sec.titulo,
                description: sec.subtitulo,
                hasMenuSection: (sec.grupos || []).map((g) => ({
                    "@type": "MenuSection",
                    name: g.grupo,
                    hasMenuItem: g.items.map((it) => ({
                        "@type": "MenuItem",
                        name: it.nombre,
                        offers: { "@type": "Offer", price: it.precio, priceCurrency: "EUR" },
                    })),
                })),
            };
        }
        return {
            "@type": "MenuSection",
            name: sec.titulo,
            description: sec.subtitulo,
            hasMenuItem: (sec.items || []).map((it) => ({
                "@type": "MenuItem",
                name: it.nombre,
                description: it.descripcion,
                offers: { "@type": "Offer", price: it.precio, priceCurrency: "EUR" },
            })),
        };
    });

    if (chuletonPrecio) {
        sections.unshift({
            "@type": "MenuSection",
            name: "Especialidad de la casa",
            hasMenuItem: [
                {
                    "@type": "MenuItem",
                    name: "Chuletón a la brasa",
                    description: "Maduración propia. Premio Restaurant Guru a la mejor parrilla.",
                    offers: { "@type": "Offer", price: chuletonPrecio, priceCurrency: "EUR" },
                },
            ],
        });
    }

    return {
        "@context": "https://schema.org",
        "@type": "Menu",
        name: "Carta — Restaurante Museo Tenería",
        inLanguage: "es-ES",
        hasMenuSection: sections,
    };
}

export const MUSEUM_LD = {
    "@context": "https://schema.org",
    "@type": ["Museum", "TouristAttraction"],
    name: "Museo Tenería — Tenería y fábrica de guantes de perrillo del siglo XV",
    description:
        "Único edificio de su clase conservado de la antigua judería de Ocaña. Tenería medieval del siglo XV donde se fabricaban los célebres guantes de perrillo para la nobleza y la realeza europea. Visitas guiadas con menú.",
    url: `${SITE_URL}/museo`,
    image: [`${SITE_URL}/og-museo.jpg`],
    address: {
        "@type": "PostalAddress",
        streetAddress: "Plaza del Mercado 3",
        addressLocality: "Ocaña",
        addressRegion: "Toledo",
        postalCode: "45300",
        addressCountry: "ES",
    },
    geo: { "@type": "GeoCoordinates", latitude: 39.9573, longitude: -3.5024 },
    isAccessibleForFree: false,
    publicAccess: true,
    tourBookingPage: `${SITE_URL}/contacto`,
};

export const FAQ_LD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "¿Dónde está el Restaurante Museo Tenería?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "En Plaza del Mercado 3, Ocaña (Toledo, 45300), a metros de la Plaza Mayor. Cerca de Aranjuez, Tarancón, Noblejas y a 45 minutos de Madrid sur.",
            },
        },
        {
            "@type": "Question",
            name: "¿Cuál es el horario del restaurante?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Lunes, miércoles y domingo: 11:00–16:30. Jueves, viernes y sábado: 11:00–16:30 y 20:00–23:00. Cerrado los martes.",
            },
        },
        {
            "@type": "Question",
            name: "¿Se puede reservar mesa online?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Sí, a través de la web en /reservas o por teléfono en el 925 48 46 33. También por WhatsApp.",
            },
        },
        {
            "@type": "Question",
            name: "¿Qué especialidades tiene la carta?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Cocina tradicional manchega y mediterránea. Especialidad: chuletón a la brasa de encina (premio Restaurant Guru a la mejor parrilla). También ibéricos de bellota, mollejas, oreja confitada, rabo de toro, pescados del Cantábrico y carta amplia de vinos de la tierra y Rioja.",
            },
        },
        {
            "@type": "Question",
            name: "¿Se hacen bodas y eventos privados?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Sí. Organizamos bodas, comuniones, bautizos y eventos de empresa con menús a medida en un edificio del siglo XV. Salones privados y combinable con visita guiada al museo.",
            },
        },
        {
            "@type": "Question",
            name: "¿Tienen museo? ¿Cuesta entrar?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Sí. La segunda planta es un museo dedicado al oficio de la tenería y a la fabricación de guantes de perrillo. Hay visitas guiadas (lunes a viernes, hasta 50 personas) con guía + menú, precio adaptado al servicio.",
            },
        },
        {
            "@type": "Question",
            name: "¿Admiten mascotas?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Sí, en la terraza-patio admitimos mascotas.",
            },
        },
        {
            "@type": "Question",
            name: "¿Qué premios tiene el Restaurante Museo Tenería?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Restaurant Guru Best Restaurant in Ocaña durante 6 años consecutivos (2021, 2022, 2023, 2024, 2025 y 2026), Nº 1 de TripAdvisor en Ocaña con 4,7★ y más de 1.053 reseñas, y mención ARQUEOPRAXIS por la rehabilitación ejemplar del edificio del siglo XV.",
            },
        },
    ],
};

export function buildBlogPostingLd(post) {
    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.titulo,
        description: post.extracto || "",
        image: post.imagen_url ? [post.imagen_url] : [`${SITE_URL}/og-default.jpg`],
        datePublished: post.fecha_pub || post.creado_en,
        dateModified: post.fecha_pub || post.creado_en,
        author: { "@type": "Organization", name: post.autor || RESTAURANT_NAME },
        publisher: {
            "@type": "Organization",
            name: RESTAURANT_NAME,
            logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
        },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
        inLanguage: "es-ES",
    };
}

export const ORGANIZATION_LD = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: RESTAURANT_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    sameAs: [
        "https://www.facebook.com/Restaurante-Museo-Tener%C3%ADa-386110368612753",
    ],
    contactPoint: [
        {
            "@type": "ContactPoint",
            telephone: "+34925484633",
            contactType: "reservations",
            areaServed: "ES",
            availableLanguage: ["Spanish"],
        },
    ],
};

export const WEBSITE_LD = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: RESTAURANT_NAME,
    inLanguage: "es-ES",
    publisher: { "@id": `${SITE_URL}/#organization` },
    potentialAction: {
        "@type": "ReserveAction",
        target: `${SITE_URL}/reservas`,
        name: "Reservar mesa",
    },
};
